// Copyright (c) 2026. CYBERKOALA.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

/**
 * Deterministic avatar color generator.
 * Generates a consistent HSL color from a string seed.
 * Same seed always produces the same color.
 * Different seeds produce visually distinct colors.
 */
export function getAvatarColor(seed: string | undefined | null, theme: 'dark' | 'light' = 'dark'): string {
  const safeSeed = typeof seed === 'string' && seed.length > 0 ? seed : 'default';

  let hash = 0;
  for (let i = 0; i < safeSeed.length; i++) {
    hash = safeSeed.charCodeAt(i) + ((hash << 5) - hash);
  }

  // HSL hue ranges that avoid problematic colors (pure red, pure green, etc.)
  const safeHueRanges: [number, number][] = [
    [10, 40],   // red → orange
    [80, 140],  // green
    [190, 260], // blue → cyan
    [270, 330], // purple → magenta
    [320, 350], // deep pink
  ];

  const absHash = Math.abs(hash);
  const range = safeHueRanges[absHash % safeHueRanges.length];
  const hue = range[0] + (absHash % (range[1] - range[0]));

  const saturation = theme === 'dark' ? 65 : 50;
  const lightness = theme === 'dark' ? 56 : 48;

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

/**
 * Get appropriate text color for contrast against the avatar background.
 */
export function getAvatarTextColor(bgHsl: string, theme: 'dark' | 'light' = 'dark'): string {
  if (!bgHsl) {
    return theme === 'dark' ? 'rgba(255,255,255,0.85)' : '#272727';
  }

  const match = bgHsl.match(/hsl\(\d+,\s*\d+%,\s*(\d+)%\)/);
  const lightness = match ? Number(match[1]) : 50;

  if (lightness < 55) {
    return '#ffffff';
  }

  return theme === 'dark' ? 'rgba(255,255,255,0.85)' : '#272727';
}

/**
 * Get metadata background color based on text color.
 */
export function getAvatarMetaBg(bgHsl: string): string {
  const match = bgHsl.match(/hsl\(\d+,\s*\d+%,\s*(\d+)%\)/);
  const lightness = match ? Number(match[1]) : 50;

  return lightness < 55 ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.6)';
}
